import React, { useEffect, useRef, useState } from 'react'

import {
  BuyerIntentClickElement as _BuyerIntentClickElement,
  BuyerIntentSessionElement as _BuyerIntentSessionElement,
  BuyerIntentViewElement as _BuyerIntentViewElement,
  buyerIntent,
} from 'buyer-intent-sdk'
import { SessionProvider } from 'buyer-intent-sdk/react'

import getConfig from 'config/public'

import { BuyerIntentTrackingProvider } from './BuyerIntentTrackingContext'
import { BUYER_INTENT_ACTIVITY_ENDPOINT } from './config'
import ErrorBoundary from './ErrorBoundary'
import { useConsentMode } from './useConsentMode'

void _BuyerIntentClickElement
void _BuyerIntentSessionElement
void _BuyerIntentViewElement

const CONSENT_TIMEOUT_MS = 3000

const useConsentOrTimeout = () => {
  const consentGranted = useConsentMode('analytics_storage')
  const [timedOut, setTimedOut] = useState(false)
  const isProductionTarget = getConfig().env === 'prod'

  useEffect(() => {
    if (isProductionTarget) {
      return undefined
    }

    const timer = setTimeout(() => setTimedOut(true), CONSENT_TIMEOUT_MS)

    return () => clearTimeout(timer)
  }, [isProductionTarget])

  return consentGranted || (!isProductionTarget && timedOut)
}

/** @type {import("react").FC<{
 *  origin?: string,
 *  children?: import("react").ReactNode,
 *  activityEndpoint?: string,
 *  userType?: string,
 *  distinctId?: string,
 * }>}
 */
const BuyerIntentSession = ({
  activityEndpoint = BUYER_INTENT_ACTIVITY_ENDPOINT,
  children,
  distinctId,
  origin,
  userType,
}) => {
  const allowed = useConsentOrTimeout()
  const initRef = useRef(false)

  useEffect(() => {
    if (allowed && !initRef.current) {
      initRef.current = true

      buyerIntent.init({
        activityEndpoint,
        distinctId,
        origin,
        userType,
      })
      buyerIntent.listenForElements()
    }
  }, [activityEndpoint, allowed, distinctId, origin, userType])

  return (
    <ErrorBoundary fallback={<>{children}</>}>
      <BuyerIntentTrackingProvider value={allowed}>
        <SessionProvider
          activityEndpoint={activityEndpoint}
          distinctId={distinctId}
          origin={origin}
          userType={userType}
        >
          {children}
        </SessionProvider>
      </BuyerIntentTrackingProvider>
    </ErrorBoundary>
  )
}

export default BuyerIntentSession
